import { useEffect, useRef, useState } from 'react';
import { FolderPlus, Pencil } from 'lucide-react';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import { Input } from '../common/Field';

/** Create a folder, or rename any item — the same small form, different verb. */
export default function NewFolderDialog({ open, onClose, onSubmit, mode = 'create', initialName = '', itemType = 'folder' }) {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setError(null);
    setBusy(false);
    setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const dot = initialName.lastIndexOf('.');
      if (mode === 'rename' && dot > 0) el.setSelectionRange(0, dot);
      else el.select();
    }, 40);
  }, [open, initialName, mode]);

  const submit = async (e) => {
    e?.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('A name is required.');
      return;
    }
    if (/[\\/:*?"<>|]/.test(trimmed)) {
      setError('A name cannot contain \\ / : * ? " < > |');
      return;
    }
    setBusy(true);
    try {
      await onSubmit(trimmed);
      onClose();
    } catch (err) {
      setError(err.message || 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const isRename = mode === 'rename';

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={440}
      title={isRename ? `Rename ${itemType}` : 'Create new folder'}
      icon={
        <div className="stat-card__icon" aria-hidden="true">
          {isRename ? <Pencil size={16} /> : <FolderPlus size={16} />}
        </div>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={busy}>
            {isRename ? 'Rename' : 'Create'}
          </Button>
        </>
      }
    >
      <form onSubmit={submit}>
        <Input
          ref={inputRef}
          label={isRename ? 'Name' : 'Folder name'}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
          error={error}
          placeholder={isRename ? '' : 'e.g. Proposals'}
          maxLength={255}
          autoComplete="off"
        />
      </form>
    </Modal>
  );
}
